import React, { useState } from "react";

import { UserOrderResponse } from "@/api";

export interface OrderDetailsEditProps {
  value: UserOrderResponse;
  onChange: (
    value: UserOrderResponse,
  ) => void;
}

export default function OrderDetailsEdit({
  value,
  onChange,
}: OrderDetailsEditProps) {
  const [error, setError] =
    useState<string | null>(null);

  const update = (
    field: keyof UserOrderResponse,
    fieldValue: string,
  ) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <section className="rounded-xl border bg-white p-6">

      <div className="grid gap-4 md:grid-cols-3">
        <Input
          label="Requestor Name"
          value={value.requestor_name ?? ""}
          onChange={(v) =>
            update("requestor_name", v)
          }
          required
        />

        <Input
          label="Email"
          type="email"
          value={value.requestor_email ?? ""}
          onChange={(v) =>
            update("requestor_email", v)
          }
          required
        />

        <Input
          label="Phone"
          type="tel"
          value={value.requestor_phone ?? ""}
          onChange={(v) =>
            update("requestor_phone", v)
          }
        />
      </div>

      {error && (
        <ErrorMessage message={error} />
      )}

    </section>
  );
}

/* --------------------------------------------------
 * Shared local UI
 * -------------------------------------------------- */

function SectionHeader({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold">
        {title}
      </h2>

      <p className="mt-1 text-sm text-gray-500">
        {description}
      </p>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label}
        {required && (
          <span className="ml-1 text-red-500">
            *
          </span>
        )}
      </label>

      <input
        type={type}
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="w-full rounded-md border px-3 py-2"
      />
    </div>
  );
}

function ErrorMessage({
  message,
}: {
  message: string;
}) {
  return (
    <div className="mt-4 text-sm text-red-600">
      {message}
    </div>
  );
}

function SectionActions({
  saving,
  onCancel,
  onSave,
}: {
  saving: boolean;
  onCancel?: () => void;
  onSave: () => void;
}) {
  return (
    <div className="mt-6 flex justify-end gap-2">
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border px-4 py-2 text-sm"
        >
          Cancel
        </button>
      )}

      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="rounded-md bg-black px-4 py-2 text-sm text-white disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
}